import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Camera, Wand2, ListChecks, ArrowRight, ShieldCheck, Smartphone, Zap,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1765158098649-d251f49ceec0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxhcmNoaXRlY3R1cmFsJTIwaW50ZXJpb3IlMjBMRUQlMjBsaWdodGluZyUyMG1vZGVybiUyMGhvbWUlMjBmYWNhZGUlMjBuaWdodCUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4OTc5ODYyOXww&ixlib=rb-4.1.0&q=85";
const IMG2 = "https://images.unsplash.com/photo-1708113388262-17fdf0e21205?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw3fHxtb2Rlcm4lMjBpbnRlcmlvciUyMExFRCUyMHN0cmlwJTIwcHJvZmlsZSUyMGFyY2hpdGVjdHVyYWwlMjBsaXZpbmclMjByb29tJTIwbGlnaHRpbmclMjBkZXNpZ258ZW58MHx8fHwxNzg5Nzk4NjM1fDA&ixlib=rb-4.1.0&q=85";
const IMG3 = "https://images.unsplash.com/photo-1666585607888-3f6fe0b323d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBpbnRlcmlvciUyMExFRCUyMHN0cmlwJTIwcHJvZmlsZSUyMGFyY2hpdGVjdHVyYWwlMjBsaXZpbmclMjByb29tJTIwbGlnaHRpbmclMjBkZXNpZ258ZW58MHx8fHwxNzg5Nzk4NjM1fDA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { icon: Camera, title: "1. Încarcă o fotografie", desc: "Fă o poză spațiului tău — interior sau fațadă — direct de pe telefon." },
  { icon: Wand2, title: "2. Vision AI + 9 randări", desc: "AI-ul analizează spațiul și generează 3 concepte odată — până la 9 randări foto LED." },
  { icon: ListChecks, title: "3. Deviz & ofertă", desc: "Primești lista de materiale defalcată, cu preț estimativ și link către magazin." },
];

const FEATURES = [
  { icon: Zap, title: "Doar produse KLUS reale", desc: "Fiecare randare folosește profile, benzi, corpuri și accesorii existente pe klusled.ro." },
  { icon: Smartphone, title: "Optimizat pentru telefon", desc: "Interfață rapidă, gândită mobile-first, cu fotografiere directă." },
  { icon: ShieldCheck, title: "Estimare + validare vânzări", desc: "Preț orientativ automat, apoi ofertă finală de la consultanții KLUS." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useT();
  const go = () => navigate(user ? "/studio" : "/register");

  return (
    <div className="min-h-screen bg-[#0D0E11]">
      <Header />

      <section className="relative overflow-hidden klus-grid-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <Badge className="bg-primary/15 text-primary border border-primary/30 mb-5">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {t("Distribuitor oficial KLUS · Vision AI")}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
              {t("Vezi cum arată")}<br />
              <span className="text-primary">{t("iluminatul LED")}</span><br />
              {t("în spațiul tău.")}
            </h1>
            <p className="text-slate-400 text-base sm:text-lg mt-6 max-w-lg leading-relaxed">
              {t("Încarcă o fotografie, iar KLUS AI Lighting Studio generează randări cu iluminat arhitectural și îți calculează lista de materiale — profile, benzi, corpuri și accesorii de pe klusled.ro.")}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button onClick={go} data-testid="hero-cta" className="h-13 py-3.5 px-6 bg-primary hover:bg-primary/90 text-white text-base">
                {t("Începe randarea")} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => window.open("https://klusled.ro/magazin/", "_blank")}
                data-testid="hero-shop" className="h-13 py-3.5 px-6 border-slate-700 text-slate-200 hover:bg-slate-800 text-base">
                {t("Vezi magazinul KLUS")}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-4">{t("Necesită cont gratuit ·")} <b className="text-slate-300">{t("9 randări foto")}</b> {t("incluse. Doriți mai multe imagini aplicate pentru proiectul dumneavoastră,")}{" "}
              <a href="https://klusled.ro/ai-un-proiect/" target="_blank" rel="noreferrer" data-testid="contact-consultant-link"
                className="text-primary underline hover:text-accent">{t("contactați")}</a> {t("un consultant KLUS.")}</p>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-3xl overflow-hidden border border-slate-800 glow-red">
              <img src="/hero-klus.jpg" alt="KLUS architectural LED lighting" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0E11] via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                <Badge className="bg-black/70 backdrop-blur text-white border-0">{t("Minimal Arhitectural")}</Badge>
                <Badge className="bg-black/70 backdrop-blur text-accent border-0">{t("Contur Liniar")}</Badge>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-3">{t("Cum funcționează")}</h2>
        <p className="text-slate-400 text-center max-w-xl mx-auto mb-12">{t("Trei pași simpli, de pe orice telefon.")}</p>
        <div className="grid md:grid-cols-3 gap-5">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="rounded-3xl border border-slate-800 bg-card p-7 hover:border-primary/40 transition-colors">
                <div className="w-12 h-12 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-2">{t(s.title)}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{t(s.desc)}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid md:grid-cols-3 gap-4">
          {[IMG2, HERO, IMG3].map((src, i) => (
            <div key={i} className="rounded-2xl overflow-hidden border border-slate-800 aspect-[4/3]">
              <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid md:grid-cols-3 gap-5">
          {FEATURES.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="flex gap-4 p-6 rounded-2xl bg-slate-900/50 border border-slate-800">
                <Icon className="w-6 h-6 text-accent shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-white mb-1">{t(s.title)}</h4>
                  <p className="text-sm text-slate-400">{t(s.desc)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 to-accent/5 p-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">{t("Gata să-ți luminezi proiectul?")}</h2>
          <p className="text-slate-400 mb-6">{t("Creează un cont și generează prima randare în câteva secunde.")}</p>
          <Button onClick={go} data-testid="footer-cta" className="h-13 py-3.5 px-8 bg-primary hover:bg-primary/90 text-white text-base">
            {t("Începe acum")} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-slate-800 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-xs text-slate-500 space-y-2">
          <p className="text-slate-400 font-medium">GLOW SENSE S.R.L. · {t("Distribuitor oficial KLUS")}</p>
          <p>CUI RO35285172 · Reg. Com. J2019001504231 · Șos. Erou Iancu Nicolae nr. 107C, Voluntari, Ilfov</p>
          <p>{t("Randările au caracter orientativ și necesită validare tehnică finală.")} © {new Date().getFullYear()} KLUS AI Lighting Studio.</p>
        </div>
      </footer>
    </div>
  );
}
